import useSwr, { SWRResponse } from 'swr';
import http from '../../core/services/http/default-http-client';
import { combineUrl } from '../../core/utilities/utilities';
import { environment } from '../../environments/environment';
import { UpdateJobItem, UpdateJobItemDto, UpdateJobItemSchema } from '../models/update-job-item';
import { jobsListCacheKey } from '../models/cache';
import { useUpdateJobsCreatedLiveData } from './use-update-jobs-created-live-hook';
import { useUpdateJobsChangedLiveData } from './use-update-jobs-changed-live-hook';
import { mapHostRunStatusCountToJobStatus } from '../mappers/device-job-status.mapper';
import { z } from 'zod';
import logger from '../../core/services/logger.service';
import { PolicyHistory, useUpdatePolicies } from './use-update-policies';
import { Device } from 'synapse-common-device-service';
import Tenant from '../../core/models/Tenant';

const UpdateJobItemArray = z.array(UpdateJobItemSchema);

const useActiveAndPastJobs = (): SWRResponse<UpdateJobItemDto[], unknown> =>
  useSwr(jobsListCacheKey, async () =>
    UpdateJobItemArray.parse(await http.get<unknown>(combineUrl(environment.frontendApiUrl, 'jobs/all')))
  );

export const useUpdateJobs = (
  tenantDevices: Device[]
): { jobs: UpdateJobItem[]; error?: unknown; loading: boolean } => {
  useUpdateJobsCreatedLiveData();
  const liveData = useUpdateJobsChangedLiveData();
  const { error, data: newData } = useActiveAndPastJobs();
  const { policies, loading } = useUpdatePolicies();
  if (error) logger.logError(error as Error);

  // let's combine the data returned by the API and the live one
  const data: UpdateJobItemDto[] | undefined = newData
    ? newData.map(item => ({
        ...item,
        ...liveData[item.id],
        status: mapHostRunStatusCountToJobStatus(item.hostRunStatusCounts)
      }))
    : undefined;

  const jobs = data ? data.map(convertJobDtoToUpdateJobItem(policies, tenantDevices)) : [];

  return { error, jobs, loading: !data || loading };
};

const convertJobDtoToUpdateJobItem =
  (policies: PolicyHistory[], tenantDevices: Device[]) =>
  (dto: UpdateJobItemDto): UpdateJobItem => {
    const jobTenants: Tenant[] = [];
    (dto.devicesNames ?? []).forEach(deviceId => {
      const jobTenantDevices = (tenantDevices ?? []).filter(device => device.id === deviceId);
      jobTenantDevices.forEach(device => {
        if (device.group?.tenant && !jobTenants.find(cellTenant => device?.group?.tenant?.id === cellTenant.uuid)) {
          jobTenants.push({
            uuid: device.group.tenant.id,
            name: device.group.tenant.name
          });
        }
      });
    });
    return {
      ...dto,
      scheduledAt: new Date(dto.scheduledAt),
      finishedAt: dto.finishedAt ? new Date(dto.finishedAt) : undefined,
      policyName: policies.find((policy: PolicyHistory) => policy.jobIds.includes(dto.id))?.name ?? '',
      updateCount:
        (dto?.osUpdateInfo?.mandatoryUpdateCount ?? 0) +
        +(dto?.osUpdateInfo?.optionalUpdateCount ?? 0) +
        (dto?.appUpdateInfo?.count ?? 0),
      tenants: jobTenants
    };
  };
